import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  Paper,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Grid,
  Button,
  IconButton,
  Chip,
  Skeleton,
  Alert,
  Divider,
  LinearProgress,
} from '@mui/material';
import {
  Checkroom as CheckroomIcon,
  Style as StyleIcon,
  Favorite as FavoriteIcon,
  TrendingUp as TrendingUpIcon,
  CheckCircle as CheckCircleIcon,
  Add as AddIcon,
  Chat as ChatIcon,
  Search as SearchIcon,
  PhotoCamera as PhotoCameraIcon,
  Visibility as VisibilityIcon,
  ChevronRight as ChevronRightIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { wardrobeAPI, outfitsAPI } from '../services/api';
import { WardrobeItem, Outfit } from '../types';
import { useAuth } from '../contexts/AuthContext';
import AnimatedNumber from '../components/AnimatedNumber';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [wardrobeItems, setWardrobeItems] = useState<WardrobeItem[]>([]);
  const [outfits, setOutfits] = useState<Outfit[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [itemsRes, outfitsRes] = await Promise.all([
        wardrobeAPI.getItems(),
        outfitsAPI.getOutfits(),
      ]);

      // Handle both array and object responses
      const items = Array.isArray(itemsRes.data) ? itemsRes.data : (itemsRes.data?.items || []);
      const outfitsList = Array.isArray(outfitsRes.data) ? outfitsRes.data : (outfitsRes.data?.outfits || []);

      setWardrobeItems(items);
      setOutfits(outfitsList);
    } catch (err: any) {
      console.error('Error fetching dashboard data:', err);
      setError(err.response?.data?.error || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  // Calculate stats
  const totalItems = wardrobeItems.length;
  const totalOutfits = outfits.length;
  const favoriteItems = wardrobeItems.filter(item => item.is_favorite);
  const totalWearCount = wardrobeItems.reduce((sum, item) => sum + (item.wear_count || 0), 0);
  const unwornItems = wardrobeItems.filter(item => !item.wear_count || item.wear_count === 0);
  const todayISO = new Date().toISOString().slice(0, 10);
  const wornTodayItems = wardrobeItems.filter(item => item.last_worn && String(item.last_worn).slice(0, 10) === todayISO);
  const recentItems = [...wardrobeItems]
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
    .slice(0, 5);
  const mostWornItems = [...wardrobeItems]
    .filter(item => item.wear_count > 0)
    .sort((a, b) => b.wear_count - a.wear_count)
    .slice(0, 5);

  // Category breakdown
  const categoryBreakdown = wardrobeItems.reduce((acc, item) => {
    acc[item.category] = (acc[item.category] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  // Utilization rate
  const utilizationRate = totalItems > 0
    ? Math.round((wardrobeItems.filter(item => item.wear_count > 0).length / totalItems) * 100)
    : 0;

  // Quick actions
  const quickActions = [
    {
      label: "Today's Outfit",
      icon: <CheckCircleIcon />,
      color: '#16a085',
      onClick: () => navigate('/fit-of-the-day')
    },
    {
      label: 'Add Item',
      icon: <AddIcon />,
      color: '#3498db',
      onClick: () => navigate('/wardrobe?action=add')
    },
    {
      label: 'Create Outfit',
      icon: <StyleIcon />,
      color: '#2ecc71',
      onClick: () => navigate('/outfits?action=create')
    },
    {
      label: 'AI Chat',
      icon: <ChatIcon />,
      color: '#9b59b6',
      onClick: () => navigate('/conversations')
    },
    {
      label: 'Search',
      icon: <SearchIcon />,
      color: '#e74c3c',
      onClick: () => navigate('/wardrobe')
    },
  ];

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ mb: 3 }}>
          <Skeleton variant="text" width={140} height={14} sx={{ mb: 1 }} />
          <Skeleton variant="text" width={300} height={48} />
        </Box>
        <Skeleton variant="rectangular" height={72} sx={{ mb: 3 }} />
        <Grid container spacing={3}>
          {[1, 2, 3, 4].map(i => (
            <Grid size={{ xs: 6, sm: 3 }} key={i}>
              <Skeleton variant="rectangular" height={108} animation="wave" />
            </Grid>
          ))}
          <Grid size={{ xs: 12, md: 6 }}>
            <Skeleton variant="rectangular" height={300} animation="wave" />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Skeleton variant="rectangular" height={300} animation="wave" />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Skeleton variant="rectangular" height={260} animation="wave" />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Skeleton variant="rectangular" height={260} animation="wave" />
          </Grid>
        </Grid>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" gutterBottom>Dashboard</Typography>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={fetchDashboardData}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      </Container>
    );
  }

  // Empty state
  if (totalItems === 0) {
    return (
      <Container maxWidth="md" sx={{ mt: 6, mb: 4 }}>
        <Paper className="fade-in-up" sx={{ p: { xs: 4, md: 6 }, textAlign: 'center' }}>
          <CheckroomIcon sx={{ fontSize: 96, color: 'secondary.main', mb: 2 }} className="pulse" />
          <Typography variant="overline" color="text.secondary">Welcome</Typography>
          <Typography variant="h3" sx={{ mt: 1, mb: 2 }}>Let's build your wardrobe</Typography>
          <Typography color="textSecondary" sx={{ mb: 4, maxWidth: 480, mx: 'auto' }}>
            Snap or drop a photo and we'll detect what you're wearing — colors, materials, style — automatically.
            Drag images anywhere on this page to start.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="large"
              startIcon={<PhotoCameraIcon />}
              onClick={() => navigate('/upload')}
            >
              Upload First Item
            </Button>
            <Button
              variant="outlined"
              size="large"
              startIcon={<ChatIcon />}
              onClick={() => navigate('/conversations')}
            >
              Talk to the Stylist
            </Button>
          </Box>
        </Paper>
      </Container>
    );
  }

  const hour = new Date().getHours();
  const greeting = hour < 5 ? 'Still up' : hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';
  const firstName = (user?.preferredName || user?.name || '').split(' ')[0];

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mb: 3 }} className="fade-in-up">
        <Box>
          <Typography variant="overline" color="text.secondary" sx={{ display: 'block', lineHeight: 1 }}>
            {greeting}{firstName ? `, ${firstName}` : ''}
          </Typography>
          <Typography variant="h4" sx={{ mt: 0.5 }}>Your Wardrobe Today</Typography>
        </Box>
        <IconButton onClick={fetchDashboardData} title="Refresh">
          <RefreshIcon />
        </IconButton>
      </Box>

      {/* Quick Actions */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 2 }}>
          Quick Actions
        </Typography>
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(5, max-content)' },
          gap: { xs: 1, sm: 1.5 },
        }}>
          {quickActions.map((action) => (
            <Button
              key={action.label}
              variant="outlined"
              startIcon={action.icon}
              onClick={action.onClick}
              fullWidth
              sx={{
                justifyContent: 'flex-start',
                py: 1.25,
                borderColor: action.color,
                color: action.color,
                '&:hover': {
                  borderColor: action.color,
                  backgroundColor: `${action.color}10`
                }
              }}
            >
              {action.label}
            </Button>
          ))}
        </Box>
      </Paper>

      <Grid container spacing={3}>
        {/* Key Stats Row */}
        <Grid size={{ xs: 6, sm: 3 }} className="stagger-item">
          <Card sx={{ height: '100%', cursor: 'pointer' }} onClick={() => navigate('/wardrobe')}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Avatar sx={{ bgcolor: '#3498db', mr: 2 }}>
                  <CheckroomIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4"><AnimatedNumber value={totalItems} /></Typography>
                  <Typography variant="body2" color="textSecondary">Items</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 6, sm: 3 }} className="stagger-item">
          <Card sx={{ height: '100%', cursor: 'pointer' }} onClick={() => navigate('/outfits')}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Avatar sx={{ bgcolor: '#2ecc71', mr: 2 }}>
                  <StyleIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4"><AnimatedNumber value={totalOutfits} /></Typography>
                  <Typography variant="body2" color="textSecondary">Outfits</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 6, sm: 3 }} className="stagger-item">
          <Card sx={{ height: '100%', cursor: 'pointer' }} onClick={() => navigate('/wardrobe?filter=favorites')}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Avatar sx={{ bgcolor: '#e74c3c', mr: 2 }}>
                  <FavoriteIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4"><AnimatedNumber value={favoriteItems.length} /></Typography>
                  <Typography variant="body2" color="textSecondary">Favorites</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 6, sm: 3 }} className="stagger-item">
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Avatar sx={{ bgcolor: '#9b59b6', mr: 2 }}>
                  <TrendingUpIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4"><AnimatedNumber value={totalWearCount} /></Typography>
                  <Typography variant="body2" color="textSecondary">Total Wears</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 6, sm: 3 }} className="stagger-item">
          <Card sx={{ height: '100%', cursor: 'pointer' }} onClick={() => navigate('/fit-of-the-day')}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Avatar sx={{ bgcolor: '#16a085', mr: 2 }}>
                  <CheckCircleIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4"><AnimatedNumber value={wornTodayItems.length} /></Typography>
                  <Typography variant="body2" color="textSecondary">Worn Today</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Wardrobe Health */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>Wardrobe Health</Typography>

            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Utilization Rate</Typography>
                <Typography variant="body2" fontWeight="bold">{utilizationRate}%</Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={utilizationRate}
                sx={{
                  height: 10,
                  borderRadius: 5,
                  backgroundColor: '#e0e0e0',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: utilizationRate > 70 ? '#2ecc71' : utilizationRate > 40 ? '#f39c12' : '#e74c3c'
                  }
                }}
              />
              <Typography variant="caption" color="textSecondary">
                {wardrobeItems.filter(item => item.wear_count > 0).length} of {totalItems} items worn at least once
              </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Grid container spacing={2}>
              <Grid size={12}>
                <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                  <Typography variant="h5" color={unwornItems.length > 5 ? 'error' : 'textPrimary'}>
                    <AnimatedNumber value={unwornItems.length} />
                  </Typography>
                  <Typography variant="caption" color="textSecondary">Unworn Items</Typography>
                </Box>
              </Grid>
            </Grid>

            {unwornItems.length > 0 && (
              <Button
                fullWidth
                sx={{ mt: 2 }}
                onClick={() => navigate('/wardrobe?filter=unworn')}
                endIcon={<ChevronRightIcon />}
              >
                View Unworn Items
              </Button>
            )}
          </Paper>
        </Grid>

        {/* Recently Added */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Recently Added</Typography>
              <Button size="small" onClick={() => navigate('/wardrobe')} endIcon={<ChevronRightIcon />}>
                View All
              </Button>
            </Box>

            {recentItems.length > 0 ? (
              <List dense>
                {recentItems.map((item) => (
                  <ListItem
                    key={item.id}
                    sx={{ cursor: 'pointer', borderRadius: 1, '&:hover': { bgcolor: 'grey.50' } }}
                    onClick={() => navigate(`/wardrobe/${item.id}`)}
                  >
                    <ListItemAvatar>
                      {item.image_url ? (
                        <Avatar src={item.image_url} variant="rounded" />
                      ) : (
                        <Avatar variant="rounded">
                          <CheckroomIcon />
                        </Avatar>
                      )}
                    </ListItemAvatar>
                    <ListItemText
                      primary={item.name}
                      secondary={
                        <Box component="span" sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <Chip label={item.category} size="small" variant="outlined" />
                          {item.brand && <Typography variant="caption">{item.brand}</Typography>}
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <IconButton edge="end" size="small">
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography color="textSecondary" sx={{ textAlign: 'center', py: 4 }}>
                No items yet
              </Typography>
            )}
          </Paper>
        </Grid>

        {/* Most Worn Items */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Most Worn Items</Typography>
            {mostWornItems.length > 0 ? (
              <List dense>
                {mostWornItems.map((item, index) => (
                  <ListItem
                    key={item.id}
                    sx={{ cursor: 'pointer', borderRadius: 1, '&:hover': { bgcolor: 'grey.50' } }}
                    onClick={() => navigate(`/wardrobe/${item.id}`)}
                  >
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: index === 0 ? '#f1c40f' : 'grey.300' }}>
                        {index + 1}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={item.name}
                      secondary={`Worn ${item.wear_count} times`}
                    />
                    {item.purchase_price && item.wear_count > 0 && (
                      <Chip
                        label={`$${(item.purchase_price / item.wear_count).toFixed(2)}/wear`}
                        size="small"
                        color={item.purchase_price / item.wear_count < 10 ? 'success' : 'default'}
                      />
                    )}
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography color="textSecondary" sx={{ textAlign: 'center', py: 4 }}>
                Start wearing items to see stats
              </Typography>
            )}
          </Paper>
        </Grid>

        {/* Category Breakdown */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Category Breakdown</Typography>
            <Box sx={{ mt: 2 }}>
              {Object.entries(categoryBreakdown)
                .sort((a, b) => b[1] - a[1])
                .slice(0, 6)
                .map(([category, count]) => {
                  const percentage = Math.round((count / totalItems) * 100);
                  return (
                    <Box key={category} sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2">{category}</Typography>
                        <Typography variant="body2" color="textSecondary">
                          {count} ({percentage}%)
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={percentage}
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                    </Box>
                  );
                })}
            </Box>
          </Paper>
        </Grid>

        {/* Suggestions Card */}
        <Grid size={12}>
          <Paper sx={{ p: 3, bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.200' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}>
                <ChatIcon />
              </Avatar>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="h6">Need outfit ideas?</Typography>
                <Typography variant="body2" color="textSecondary">
                  Chat with our AI stylist to get personalized recommendations based on your wardrobe, the weather, and occasion.
                </Typography>
              </Box>
              <Button
                variant="contained"
                onClick={() => navigate('/conversations')}
                endIcon={<ChevronRightIcon />}
              >
                Start Chat
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Dashboard;
